// Recency ordering for the "Recent Boards" widget (FE#243).
//
// `updatedAt` (falling back to `createdAt`) is a Mongo timestamp that reaches the
// store typed inconsistently — an ISO string on persisted docs, but a `Date` or
// `Moment` on some paths, and `undefined` on optimistically-created / projected
// rows (see DBServiceReducers.ts:58-61). The widget used to hand these straight
// to sift-sort, whose comparator is `String(a).localeCompare(b)`:
//   - `undefined` -> "undefined", which sorts AFTER every "2026-…" ISO string and
//     so floated stale/optimistic boards to the TOP under descending order.
//   - a `Date`/`Moment` -> "Tue Aug 18 2026 …", which collates by weekday/month
//     name, not chronologically.
// Both broke the "most-recent-first" ordering. Normalizing to epoch-ms fixes it.

type RecencyFields = { updatedAt?: unknown; createdAt?: unknown };

function timestampMs(v: unknown): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const ms = v.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof v === 'string' || typeof v === 'number') {
    const ms = new Date(v).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  // Moment / Date-like: anything whose valueOf() yields epoch ms.
  if (typeof v === 'object') {
    const ms: unknown = (v as { valueOf: () => unknown }).valueOf();
    return typeof ms === 'number' && !Number.isNaN(ms) ? ms : null;
  }
  return null;
}

/**
 * Normalize a board's recency signal to epoch-ms. Prefers `updatedAt`, falls
 * back to `createdAt`. Returns `Number.NEGATIVE_INFINITY` when there is no usable
 * timestamp, so timestamp-less rows sink to the bottom instead of the top.
 */
export function boardRecencyMs(board: RecencyFields | null | undefined): number {
  if (board == null) return Number.NEGATIVE_INFINITY;
  const ms = timestampMs(board.updatedAt) ?? timestampMs(board.createdAt);
  return ms ?? Number.NEGATIVE_INFINITY;
}

/**
 * Order boards most-recently-updated first for the Recent Boards widget.
 * Stable: boards with equal recency keep their input order.
 */
export function sortBoardsByRecency<T extends RecencyFields>(boards: readonly T[]): T[] {
  return boards
    .map((board, index) => ({ board, index }))
    .sort((a, b) => {
      // Compare (not subtract) so equal NEGATIVE_INFINITY recencies don't yield NaN.
      const msA = boardRecencyMs(a.board);
      const msB = boardRecencyMs(b.board);
      if (msA !== msB) return msB > msA ? 1 : -1;
      return a.index - b.index;
    })
    .map((entry) => entry.board);
}
